/**
 * walletPass.js — adding a visit pass to a phone wallet. v242.88.0
 *
 * ★★★ THIS FILE EXISTS BECAUSE THE DECISION LIVED IN TWO PLACES AND THE CODE IN
 * TWO MORE. `WalletPassModal.jsx` and `CheckInModal.jsx` each had their own
 * `WALLET_ENABLED` constant and their own `<a href>` to the API. Fixing one left
 * the other, and that is exactly what happened: v242.87.0 hid the button on one
 * surface and never touched the other.
 *
 * ★★★ WHAT IS ACTUALLY CONFIGURED, MEASURED 2026-08-20 AGAINST PRODUCTION.
 * The route checks its five signing env vars BEFORE it looks the case up
 * (api/index.py:43761), so a bogus case id is a complete experiment that needs
 * no real data — 501 means the certs are missing, anything else means they are
 * present:
 *
 *   curl -s -o /dev/null -w '%{http_code}\n' \
 *     https://api.orellahealth.com/api/v1/cases/00000000-0000-0000-0000-000000000000/wallet/apple.pkpass
 *   -> 404  ("No active check-in pass")  = CERTS ARE CONFIGURED
 *
 *   ...same path, /wallet/google
 *   -> 501  ("wallet_not_configured")    = NOT configured
 *
 * ★ Since orella-api D5c (v243.284.0, R08 · PROD-212) a logged-out probe answers 401
 *   (wallet_auth_required) BEFORE the cert check, so the probe now needs a test patient's
 *   bearer (or a signed link) for a case of theirs; a foreign case answers 403.
 *
 * ★★ RE-RUN THOSE TWO COMMANDS BEFORE CHANGING EITHER FLAG. A flag whose
 * comment asserts a server condition and names no way to re-check it is how the
 * previous one went stale for versions (§1bl(e)).
 */
import { Capacitor } from '@capacitor/core'
import { API_BASE, getToken, casesAPI } from '../services/api'
import { saveOrShareBlob } from './saveOrShareBlob'

// Apple: signing certs are live (see the 404 probe above). v243.8.0 of the API.
export const APPLE_WALLET_ENABLED = true

// Google: the API still answers 501 — it needs an issuer ID and a service
// account. Re-run the probe above; when it stops returning 501, flip this and
// implement the save-link flow, which is a JWT redirect and must be opened with
// @capacitor/browser rather than an anchor.
export const GOOGLE_WALLET_ENABLED = false

/**
 * Fetch the signed .pkpass and hand it to the OS.
 *
 * ★★★ WHY NOT AN <a href>, WHICH IS WHAT THIS REPLACED AND WHICH "WORKED":
 * it worked in the sense that Wallet took the pass. It also NAVIGATED THE WEB
 * VIEW to the pkpass URL, so when the patient came back there was no app
 * underneath — just a blank page, and then the sign-in screen once the reload
 * found empty storage. Dr. Rami reported it as "stuck on a weblink", which is
 * the precise description.
 *
 * Fetching the bytes and handing them to Share.share() keeps the SPA mounted the
 * whole time: the share sheet is presented OVER the app and dismissed back into
 * it. Nothing navigates.
 *
 * ★ R08 · PROD-212 (P2-1): the route IS gated now (orella-api D5c S9, v243.284.0):
 *   only the case's own patient, by bearer or by a 300 s signed link from
 *   POST /cases/<id>/wallet/link. Web sends the bearer; native asks for the
 *   signed link, because the in-app browser cannot carry an Authorization header.
 *
 * @returns {Promise<void>} rejects with a human-readable Error the caller shows.
 */
export async function addAppleWalletPass(caseId) {
  if (!caseId) throw new Error('No case id for the wallet pass.')
  const url = `${API_BASE}/cases/${caseId}/wallet/apple.pkpass`

  // ★★★ v242.106.0 — NATIVE OPENS THE URL, IT DOES NOT FETCH THE BYTES.
  //   TF b39 #7: "Add to apple wallet results in a sharelike state." It did:
  //   Share.share() presents the SHARE sheet, which is the correct way to send
  //   a file to somebody and the wrong way to hand one to iOS. Opened as a URL
  //   in an SFSafariViewController, iOS recognises application/vnd.apple.pkpass
  //   and presents its own "Add to Apple Wallet" sheet.
  //   ★★ THE SERVER HALF IS NOT OPTIONAL: api v243.107.0 changed this route's
  //      Content-Disposition from `attachment` to `inline`. With `attachment`,
  //      Safari SAVES the pass to Files and never offers Wallet — which is the
  //      share sheet again, wearing a different hat.
  //   ★ @capacitor/browser presents OVER the app; the SPA stays mounted. That
  //     is the whole reason the old `<a href>` had to go (see above) and this
  //     does not reintroduce it.
  //   ★★ R08 · PROD-212 (P2-1): the bare URL is REFUSED since orella-api D5c (401
  //      wallet_auth_required), and Browser.open cannot send a bearer. So native first
  //      asks the API (with the bearer) for a short-lived signed link and opens THAT.
  //      A link that is not this case's apple.pkpass path with exp and sig is not opened.
  if (Capacitor.isNativePlatform()) {
    let link
    try {
      link = await casesAPI.walletLink(caseId)
    } catch (err) {
      throw walletFailure(err?.status, err?.error)
    }
    const signed = walletLinkUrl(API_BASE, caseId, link?.applePath)
    if (!signed) throw new Error('Could not build the wallet pass.')
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url: signed, presentationStyle: 'popover' })
    return
  }

  // Web: fetch and download, unchanged. A desktop browser has a real download
  // manager, and this path was never the one reported.
  const token = getToken()
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  if (!res.ok) {
    let detail = ''
    try { detail = (await res.json())?.error || '' } catch (_) { /* not json */ }
    throw walletFailure(res.status, detail)
  }
  const blob = await res.blob()
  await saveOrShareBlob(blob, `orella-visit-${String(caseId).slice(0, 8)}.pkpass`)
}

// One wording for a refused pass, web and native (unchanged strings).
function walletFailure(status, detail) {
  if (status === 501) return new Error('Apple Wallet passes are not switched on yet.')
  if (status === 404) return new Error('This visit has no check-in pass yet.')
  return new Error(detail ? `Wallet pass failed (${detail}).` : 'Could not build the wallet pass.')
}

/**
 * R08 · PROD-212 (P2-1): the absolute URL of a signed pass link, or null.
 * The API answers a PATH (/api/v1/cases/<id>/wallet/apple.pkpass?exp=…&sig=…); it is prefixed
 * with the API origin (API_BASE minus its /api/v1). Anything else — another case, another host,
 * no exp or sig — is null, and nothing is opened.
 */
export function walletLinkUrl(apiBase, caseId, applePath) {
  if (typeof applePath !== 'string' || !caseId) return null
  const m = /^\/api\/v1\/cases\/([^/?#]+)\/wallet\/apple\.pkpass\?([^#]*)$/.exec(applePath)
  if (!m || m[1].toLowerCase() !== String(caseId).toLowerCase()) return null
  const q = new URLSearchParams(m[2])
  if (!/^\d+$/.test(q.get('exp') || '') || !/^[0-9a-f]{64}$/i.test(q.get('sig') || '')) return null
  const origin = String(apiBase || '').replace(/\/api\/v1\/?$/, '')
  return `${origin}${applePath}`
}
